import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "../context/I18nContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LinkIcon from "@mui/icons-material/Link";
import { FacerevealResult } from "../models/Result";
import ResultThumbnail from "./ResultThumbnail";
import faceRecognitionIcon from "../assets/facial-recognition.png";
import { useContextData } from "../hooks/useContextData";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const SOURCE_LABELS = [
  "PUBLIC RECORD INDEX",
  "SOCIAL MEDIA ARCHIVE",
  "EMPLOYMENT REGISTRY",
  "HISTORICAL DATA CACHE",
  "GOVERNMENT DATABASE",
  "NEWS ARCHIVE",
  "WEB INDEX",
];

const INSTANCE_PREFIXES = [
  "Alpha",
  "Beta",
  "Delta",
  "Gamma",
  "Epsilon",
  "Zeta",
  "Eta",
];

function getScoreColor(score: number): string {
  if (score >= 90) return "#00c896";
  if (score >= 75) return "#00a86b";
  if (score >= 60) return "#f0a500";
  return "#e05a5a";
}

const CopyableUrl = ({
  value,
  tCopied,
  tCopyUrl,
}: {
  value: string;
  tCopied: string;
  tCopyUrl: string;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayUrl =
    value.replace(/^https?:\/\//, "").slice(0, 38) +
    (value.length > 38 ? "…" : "");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <LinkIcon sx={{ fontSize: 14, color: "primary.main", flexShrink: 0 }} />
      <Tooltip title={value}>
        <Typography
          component="a"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "0.8rem",
            color: "primary.main",
            textDecoration: "none",
            fontFamily: '"Inter", sans-serif',
            "&:hover": { textDecoration: "underline" },
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {displayUrl}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? tCopied : tCopyUrl}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default function ResultTable({
  result,
  handleCompareFaces,
  selectedImage,
  onExpand,
}: {
  result: FacerevealResult[];
  handleCompareFaces: (toCompare: FacerevealResult) => Promise<void>;
  selectedImage: string | null;
  onExpand: (imageUrl: string) => void;
}) {
  const [isExporting, setIsExporting] = useState(false);
  const [loadingRowKey, setLoadingRowKey] = useState<string | null>(null);
  const { setSelectedResult, theme } = useContextData();
  const muiTheme = useTheme();
  const isDark = theme === "dark";
  const { t } = useTranslation();

  const MOCK_RESULTS: FacerevealResult[] = [
    {
      score: 95,
      url: "https://linkedin.com/in/user_9921",
      image_key: "https://i.pravatar.cc/150?img=1",
      date: "2026-03-21",
      similarity: 94.32,
      confidence: 99.1,
    },
    {
      score: 83,
      url: "https://instagram.com/p/B_X9z...",
      image_key: "https://i.pravatar.cc/150?img=2",
      date: "2026-03-21",
      similarity: 81.5,
      confidence: 97.4,
    },
    {
      score: 76,
      url: "https://corp-directory.org/staff...",
      image_key: "https://i.pravatar.cc/150?img=3",
      date: "2026-03-21",
    },
    {
      score: 61,
      url: "https://archive.web/user_snapshot",
      image_key: "https://i.pravatar.cc/150?img=4",
      date: "2026-03-21",
    },
  ];

  const useMock = process.env.REACT_APP_MOCK_RESULTS === "true";
  const displayedResult = useMock ? MOCK_RESULTS : result;

  const toBase64 = (url: string): Promise<string> =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = () => resolve("");
      img.src = url;
    });

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Face Reveal Results", 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 14, 22);
      const ROW_IMG_SIZE = 18;
      const rowImages = await Promise.all(
        displayedResult.map((row) => toBase64(row.image_key)),
      );
      const drawTable = (startY: number) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        autoTable(doc, {
          startY,
          head: [["Photo", "#", "Score", "URL", "Similarity", "Confidence"]],
          body: displayedResult.map((row, index) => [
            "",
            index + 1,
            `${row.score}%`,
            row.url,
            row.similarity != null ? `${row.similarity.toFixed(2)}%` : "-",
            row.confidence != null ? `${row.confidence.toFixed(2)}%` : "-",
          ]),
          styles: {
            fontSize: 9,
            cellPadding: 3,
            minCellHeight: ROW_IMG_SIZE + 4,
          },
          headStyles: { fillColor: [0, 65, 82] },
          columnStyles: {
            0: { cellWidth: ROW_IMG_SIZE + 4 },
            3: { cellWidth: 70 },
          },
          didDrawCell: (data) => {
            if (data.section === "body" && data.column.index === 0) {
              const b64 = rowImages[data.row.index];
              if (b64) {
                const padding = 2;
                doc.addImage(
                  b64,
                  "JPEG",
                  data.cell.x + padding,
                  data.cell.y + padding,
                  ROW_IMG_SIZE,
                  ROW_IMG_SIZE,
                );
              }
            }
          },
          didDrawPage: () => {
            const totalPages = (doc.internal as any).getNumberOfPages();
            const currentPage = (doc.internal as any).getCurrentPageInfo()
              .pageNumber;
            // Footer link
            doc.setFontSize(8);
            doc.setTextColor(0, 65, 82);
            const linkText = "www.facerevealer.com";
            const textWidth = doc.getTextWidth(linkText);
            const x = (pageWidth - textWidth) / 2;
            const y = pageHeight - 6;
            doc.textWithLink(linkText, x, y, {
              url: "https://www.facerevealer.com",
            });
            // Page number on the right
            doc.setTextColor(150);
            doc.text(`${currentPage} / ${totalPages}`, pageWidth - 14, y, {
              align: "right",
            });
            // Reset color
            doc.setTextColor(0);
          },
        });
        doc.save(`facereveal-results-${Date.now()}.pdf`);
      };
      if (selectedImage) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            const imgWidth = 35;
            const imgHeight = (img.naturalHeight / img.naturalWidth) * imgWidth;
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.addImage(
              img as any,
              "JPEG",
              pageWidth - imgWidth - 10,
              8,
              imgWidth,
              imgHeight,
            );
            drawTable(Math.max(28, imgHeight + 12));
            resolve();
          };
          img.onerror = () => {
            drawTable(28);
            resolve();
          };
          img.src = selectedImage;
        });
      } else {
        drawTable(28);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const bgPaper = isDark ? "#0d1117" : muiTheme.palette.background.paper;
  const bgHeader = isDark ? "#161b22" : muiTheme.palette.grey[100];
  const borderColor = isDark
    ? "rgba(255,255,255,0.08)"
    : muiTheme.palette.divider;
  const textSecondary = muiTheme.palette.text.secondary;

  return (
    <Box>
      {/* Scan Analysis header */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: bgPaper,
          border: `1px solid ${borderColor}`,
          borderRadius: 3,
          p: 3,
          mb: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
        }}
      >
        {selectedImage && (
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Box
              component="img"
              src={selectedImage}
              alt={t("results.input_source")}
              onClick={() => onExpand(selectedImage)}
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                borderRadius: 2,
                cursor: "zoom-in",
                border: `2px solid ${muiTheme.palette.primary.main}`,
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                textAlign: "center",
                fontSize: "0.55rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                bgcolor: "rgba(0,65,82,0.85)",
                color: "#fff",
                py: 0.25,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              }}
            >
              {t("results.input_source")}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              color: "primary.main",
              fontFamily: '"Inter", sans-serif',
              mb: 0.5,
            }}
          >
            {t("results.extraction_id")}: #DF-
            {Math.floor(10000 + displayedResult.length * 1234)}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 800,
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            {t("results.scan_analysis")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: textSecondary,
              fontFamily: '"Inter", sans-serif',
              lineHeight: 1.6,
            }}
          >
            {t("results.scan_description", { count: displayedResult.length })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdfIcon fontSize="small" />}
          onClick={handleExportPDF}
          disabled={isExporting}
          sx={{
            flexShrink: 0,
            borderColor: borderColor,
            color: textSecondary,
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.05em",
            "&:hover": { borderColor: "primary.main", color: "primary.main" },
          }}
        >
          {isExporting ? t("results.exporting") : t("results.export_pdf")}
        </Button>
      </Paper>

      {/* Results table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: bgPaper,
          border: `1px solid ${borderColor}`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: bgHeader }}>
              {[
                t("results.col_identity_map"),
                t("results.col_verification_path"),
                t("results.col_bio_match_score"),
                t("results.col_action"),
              ].map((h: string) => (
                <TableCell
                  key={h}
                  sx={{
                    borderBottom: `1px solid ${borderColor}`,
                    py: 1.5,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: textSecondary,
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedResult.map((row, index) => {
              const label = `Instance ${INSTANCE_PREFIXES[index % INSTANCE_PREFIXES.length]}-${index + 1}`;
              const sourceLabel = SOURCE_LABELS[index % SOURCE_LABELS.length];
              const scoreColor = getScoreColor(row.score);

              return (
                <TableRow
                  key={row.image_key}
                  sx={{
                    borderBottom: `1px solid ${borderColor}`,
                    "&:last-child td": { border: 0 },
                    "&:hover": {
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  {/* Identity Map */}
                  <TableCell
                    sx={{ py: 2, borderBottom: `1px solid ${borderColor}` }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ResultThumbnail
                        src={row.image_key}
                        alt={t("results.match_thumbnail")}
                        onClick={() => onExpand(row.image_key)}
                      />
                      <Box>
                        {/* <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            fontFamily: '"Manrope", sans-serif',
                          }}
                        >
                          {label}
                        </Typography> */}
                        {/* <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: textSecondary,
                            letterSpacing: "0.06em",
                            fontFamily: '"Inter", sans-serif',
                          }}
                        >
                          {sourceLabel}
                        </Typography> */}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* URL */}
                  <TableCell
                    sx={{
                      py: 2,
                      borderBottom: `1px solid ${borderColor}`,
                      maxWidth: 260,
                    }}
                  >
                    <CopyableUrl
                      value={row.url}
                      tCopied={t("results.copied")}
                      tCopyUrl={t("results.copy_url")}
                    />
                  </TableCell>

                  {/* Score */}
                  <TableCell
                    sx={{
                      py: 2,
                      borderBottom: `1px solid ${borderColor}`,
                      minWidth: 160,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={row.score}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: scoreColor,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: scoreColor,
                          fontFamily: '"Manrope", sans-serif',
                          minWidth: 42,
                        }}
                      >
                        {row.score}%
                      </Typography>
                    </Box>
                    {row.similarity != null && (
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: textSecondary,
                          mt: 0.5,
                          fontFamily: '"Inter", sans-serif',
                        }}
                      >
                        Similarity {row.similarity.toFixed(1)}% · Confidence{" "}
                        {row.confidence?.toFixed(1)}%
                      </Typography>
                    )}
                  </TableCell>

                  {/* Action */}
                  <TableCell
                    sx={{ py: 2, borderBottom: `1px solid ${borderColor}` }}
                  >
                    {row.similarity ? (
                      <Chip
                        label={t("results.verified")}
                        size="small"
                        sx={{
                          bgcolor: "rgba(0,200,150,0.12)",
                          color: "#00c896",
                          fontWeight: 700,
                          fontSize: "0.6rem",
                          letterSpacing: "0.06em",
                          height: 22,
                        }}
                      />
                    ) : row.score > 10 ? (
                      <Tooltip title={t("results.details_tooltip")}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={loadingRowKey === row.image_key}
                          onClick={async () => {
                            setLoadingRowKey(row.image_key);
                            try {
                              await handleCompareFaces(row);
                            } finally {
                              setLoadingRowKey(null);
                            }
                          }}
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            fontFamily: '"Inter", sans-serif',
                            borderColor: borderColor,
                            color: textSecondary,
                            minWidth: 100,
                            "&:hover": {
                              borderColor: "primary.main",
                              color: "primary.main",
                            },
                          }}
                        >
                          {loadingRowKey === row.image_key ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <CircularProgress
                                size={12}
                                thickness={5}
                                color="inherit"
                              />
                              {t("results.details_loading")}
                            </Box>
                          ) : (
                            t("results.details")
                          )}
                        </Button>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1.5,
          px: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: textSecondary,
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {t("results.displaying", {
            count: displayedResult.length,
            total: displayedResult.length,
          })}
        </Typography>
      </Box>
    </Box>
  );
}
